<template>
  <div
    class="file-drop-zone"
    :class="{ 'drag-over': isDragOver, 'has-file': hasFile }"
    @dragover.prevent="handleDragOver"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop"
    @click="handleClick"
  >
    <input
      ref="fileInput"
      type="file"
      :accept="accept"
      :multiple="multiple"
      class="hidden"
      @change="handleFileChange"
    />

    <div v-if="!hasFile" class="drop-zone-content">
      <svg class="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
        />
      </svg>
      <p class="drop-text">{{ text }}</p>
      <p class="drop-subtext">{{ subtext }}</p>
    </div>

    <div v-else class="file-info">
      <svg class="file-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      <div class="file-details">
        <p class="file-name">{{ fileName }}</p>
        <p class="file-meta">{{ fileSize }} • {{ pageCount }} {{ $t('common.pages', pageCount) }}</p>
      </div>
      <button @click.stop="clearFile" class="clear-btn">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

interface Props {
  text: string
  subtext?: string
  accept?: string
  multiple?: boolean
  modelValue?: File | File[] | null
}

interface Emits {
  (e: 'update:modelValue', value: File | File[] | null): void
  (e: 'file-loaded', info: { pageCount: number; size: string }): void
}

const props = withDefaults(defineProps<Props>(), {
  accept: 'application/pdf',
  multiple: false,
  modelValue: null,
})

const emit = defineEmits<Emits>()

const fileInput = ref<HTMLInputElement>()
const isDragOver = ref(false)

const hasFile = computed(() => {
  if (props.multiple) {
    const files = props.modelValue as File[]
    return files && files.length > 0
  }
  return props.modelValue !== null
})

const fileName = computed(() => {
  if (props.multiple) {
    const files = props.modelValue as File[]
    return files.length === 1 ? files[0].name : `${files.length} files selected`
  }
  return (props.modelValue as File)?.name || ''
})

const fileSize = computed(() => {
  if (props.multiple) {
    const files = props.modelValue as File[]
    return formatFileSize(files.reduce((acc, f) => acc + f.size, 0))
  }
  return formatFileSize((props.modelValue as File)?.size || 0)
})

const pageCount = ref(0)

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

function handleDragOver(e: DragEvent) {
  isDragOver.value = true
}

function handleDragLeave(e: DragEvent) {
  isDragOver.value = false
}

function handleDrop(e: DragEvent) {
  isDragOver.value = false
  const files = e.dataTransfer?.files
  if (files) {
    processFiles(files)
  }
}

function handleClick() {
  fileInput.value?.click()
}

function handleFileChange(e: Event) {
  const target = e.target as HTMLInputElement
  const files = target.files
  if (files) {
    processFiles(files)
  }
}

async function processFiles(files: FileList) {
  if (props.multiple) {
    const fileList = Array.from(files)
    emit('update:modelValue', fileList)

    // Load info for first file
    if (fileList.length > 0) {
      loadPdfInfo(fileList[0])
    }
  } else {
    const file = files[0]
    emit('update:modelValue', file)
    await loadPdfInfo(file)
  }
}

async function loadPdfInfo(file: File) {
  try {
    const arrayBuffer = await file.arrayBuffer()
    const { getPageCount } = await import('@/utils/pdfSplit')
    const pages = await getPageCount(arrayBuffer)
    pageCount.value = pages
    emit('file-loaded', { pageCount: pages, size: formatFileSize(file.size) })
  } catch (err) {
    console.error('Failed to load PDF info:', err)
  }
}

function clearFile() {
  emit('update:modelValue', props.multiple ? [] : null)
  pageCount.value = 0
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}
</script>

<style scoped>
.file-drop-zone {
  @apply border-2 border-dashed border-gray-300 rounded-lg p-8 cursor-pointer transition-all duration-200;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-drop-zone:hover {
  @apply border-indigo-400 bg-indigo-50;
}

.file-drop-zone.drag-over {
  @apply border-indigo-600 bg-indigo-100;
}

.file-drop-zone.has-file {
  @apply border-solid border-gray-200;
}

.drop-zone-content {
  @apply text-center;
}

.upload-icon {
  @apply w-16 h-16 mx-auto mb-4 text-gray-400;
}

.drop-text {
  @apply text-lg font-medium text-gray-700 mb-2;
}

.drop-subtext {
  @apply text-sm text-gray-500;
}

.file-info {
  @apply flex items-center gap-4 w-full;
}

.file-icon {
  @apply w-12 h-12 text-red-500 flex-shrink-0;
}

.file-details {
  @apply flex-1 min-w-0;
}

.file-name {
  @apply font-medium text-gray-900 truncate;
}

.file-meta {
  @apply text-sm text-gray-500;
}

.clear-btn {
  @apply p-2 text-gray-400 hover:text-red-600 transition-colors;
}

.clear-btn:hover {
  @apply bg-red-50 rounded-lg;
}

.hidden {
  display: none;
}
</style>
